// One choice step's fields: prompt, the scale preset that expands to
// options, whether this step is the survey's score, and the option rows.
// Presets are admin-side sugar (scale-presets.ts) — the wire only ever sees
// options. Turning "Use as score" on is exclusive across the list; the
// parent flips the others off (onToggleMetric).

import { Input, Select, Switch } from "@pej/kore/components";
import { type UseFormReturn, useFieldArray } from "react-hook-form";
import { matchingPreset, presetByKey, SCALE_PRESETS } from "@/features/presets/scale-presets";
import { Field } from "@/lib/forms";
import { ChoiceOptionsEditor } from "./choice-options-editor";
import { StepDescriptionField } from "./step-description-field";
import { optionFormToChoiceOption, type StepsFormValues } from "./survey-schema";

const CUSTOM_PRESET = "custom";

export interface ChoiceStepFieldsProps {
    form: UseFormReturn<StepsFormValues>;
    stepIndex: number;
    onToggleMetric: (next: boolean) => void;
}

export function ChoiceStepFields({ form, stepIndex, onToggleMetric }: ChoiceStepFieldsProps) {
    const optionsArray = useFieldArray({ control: form.control, name: `steps.${stepIndex}.options` });
    const options = form.watch(`steps.${stepIndex}.options`);
    const isMetric = form.watch(`steps.${stepIndex}.isMetric`);
    const selectedPresetKey =
        matchingPreset(options.map((option) => optionFormToChoiceOption(option)))?.key ?? CUSTOM_PRESET;

    function applyPreset(nextValue: unknown) {
        const key = String(nextValue);
        if (key === CUSTOM_PRESET) return;
        const preset = presetByKey(key);
        if (!preset) return;
        // `replace`, not `setValue`: the field array's own replace is what
        // keeps its tracked rows in sync with a whole-array swap (RHF gotcha).
        optionsArray.replace(
            preset.options.map((option) => ({
                key: option.key,
                label: option.label ?? "",
                icon: option.icon ?? "",
                assetId: option.assetId ?? "",
                assetUrl: option.assetUrl ?? "",
                value: option.value ?? 0,
            })),
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <Field
                name={`steps.${stepIndex}.prompt`}
                control={form.control}
                label="Prompt"
                required
                render={({ field }) => <Input {...field} placeholder="How was your visit?" />}
            />
            <StepDescriptionField control={form.control} name={`steps.${stepIndex}.description`} />
            <div className="flex items-center justify-between gap-4 rounded-kore-md border border-kore-border p-3">
                <div>
                    <p className="text-sm font-medium text-kore-text">Use as score</p>
                    <p className="text-xs text-kore-text-muted">
                        The scored step feeds the average and the workspace overview. One per survey.
                    </p>
                </div>
                <Switch checked={isMetric} onCheckedChange={onToggleMetric} aria-label="Use as score" />
            </div>
            {isMetric && (
                <p className="text-xs text-kore-text-muted">
                    Past responses keep the score they were submitted with. Changing these values, or moving the score
                    to another step, does not rewrite them.
                </p>
            )}
            <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-kore-text">Scale</span>
                <Select value={selectedPresetKey} onValueChange={applyPreset}>
                    <Select.Trigger aria-label="Scale">
                        <Select.Value>
                            {(v: unknown) => SCALE_PRESETS.find((preset) => preset.key === v)?.label ?? "Custom"}
                        </Select.Value>
                    </Select.Trigger>
                    <Select.Content>
                        <Select.Item value={CUSTOM_PRESET}>Custom</Select.Item>
                        {SCALE_PRESETS.map((preset) => (
                            <Select.Item key={preset.key} value={preset.key}>
                                {preset.label}
                            </Select.Item>
                        ))}
                    </Select.Content>
                </Select>
                <p className="text-xs text-kore-text-muted">Pick a starting point, then adjust any option below.</p>
            </div>
            <ChoiceOptionsEditor
                control={form.control}
                stepIndex={stepIndex}
                scored={isMetric}
                fields={optionsArray.fields}
                append={optionsArray.append}
                remove={optionsArray.remove}
            />
        </div>
    );
}
